/* ============================================================
   teg — the shell.

   One machine, seven ways of looking at it. The shell owns the
   session, the poll loop and which view is showing; each view is
   handed data rather than fetching its own, so the page makes one
   round of requests every few seconds no matter how much is on
   screen.

   The backend's /api/stats blocks half a second sampling CPU, so
   the loop is deliberately unhurried and pauses while the tab is
   hidden — a dashboard left open on a second monitor should not
   be the busiest client this box has.
   ============================================================ */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import './teg.css';
import { Icon, fmtUptime } from './lib';
import { api, setUnauthorizedHandler } from './api';
import Login from './Login';
import Overview from './Overview';
import System from './System';
import Storage from './Storage';
import Services from './Services';
import Games from './Games';
import Processes from './Processes';
import Logs from './Logs';

const TOKEN_KEY = 'tegtech_token';
const POLL_MS = 5000;
const HISTORY = 90;

const VIEWS = [
  ['overview', 'Overview', 'activity'],
  ['system', 'System', 'cpu'],
  ['storage', 'Storage', 'disk'],
  ['services', 'Services', 'server'],
  ['games', 'Games', 'game'],
  ['processes', 'Processes', 'list'],
  ['logs', 'Logs', 'logs'],
];

export default function TegApp() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [view, setView] = useState(() => window.location.hash.replace(/^#\/?/, '') || 'overview');
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState(null);
  const [processes, setProcesses] = useState(null);
  const [games, setGames] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const history = useRef({ cpu: [], ram: [], swap: [], netIn: [], netOut: [], dRead: [], dWrite: [] });

  const toast = useCallback((kind, text) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5200);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      localStorage.removeItem(TOKEN_KEY);
      setToken('');
      setError('The dashboard password changed — sign in again.');
    });
  }, []);

  useEffect(() => {
    const onHash = () => setView(window.location.hash.replace(/^#\/?/, '') || 'overview');
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const go = (v) => { window.location.hash = `#/${v}`; setView(v); setNavOpen(false); };

  const push = (key, value) => {
    if (value == null || Number.isNaN(value)) return;
    const arr = history.current[key];
    arr.push(value);
    if (arr.length > HISTORY) arr.shift();
  };

  const poll = useCallback(async (signal) => {
    if (!token) return;
    try {
      const [s, sv, pr] = await Promise.all([
        api.stats(token, signal),
        api.services(token, signal),
        api.processes(token, signal),
      ]);
      setStats(s); setServices(sv); setProcesses(pr); setError('');
      push('cpu', s.cpu?.avg);
      push('ram', s.memory?.percent);
      push('swap', s.swap?.total ? (s.swap.used / s.swap.total) * 100 : 0);
      push('netIn', s.network?.recv_per_s || 0);
      push('netOut', s.network?.sent_per_s || 0);
      push('dRead', s.disk_io?.read_per_s || 0);
      push('dWrite', s.disk_io?.write_per_s || 0);
    } catch (e) {
      if (e.name !== 'AbortError' && e.status !== 401) setError(e.message);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    const ctrl = new AbortController();
    poll(ctrl.signal);
    const id = setInterval(() => { if (!document.hidden) poll(ctrl.signal); }, POLL_MS);
    return () => { ctrl.abort(); clearInterval(id); };
  }, [token, poll]);

  // Games are polled more slowly: three HTTP hops behind this one call.
  const pollGames = useCallback(async () => {
    if (!token) return;
    try { const g = await api.games(token); setGames(g.games || []); }
    catch { /* the view shows its own state */ }
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    pollGames();
    const id = setInterval(() => { if (!document.hidden) pollGames(); }, 15000);
    return () => clearInterval(id);
  }, [token, pollGames]);

  const refresh = async () => {
    setBusy(true);
    await Promise.all([poll(), pollGames()]);
    setBusy(false);
  };

  const signIn = (t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); setError(''); };
  const signOut = () => { localStorage.removeItem(TOKEN_KEY); setToken(''); };

  if (!token) return <Login onLogin={signIn} note={error}/>;

  const shared = { token, stats, services, processes, games, history: history.current, toast, refresh, onGamesChanged: pollGames };

  return (
    <div className={`tg-app ${navOpen ? 'is-nav-open' : ''}`}>
      <header className="tg-head">
        <button type="button" className="iconbtn tg-navtoggle" aria-label="Sections"
                onClick={() => setNavOpen((v) => !v)}>
          <Icon name="menu" size={18}/>
        </button>
        <span className="tg-brand">teg<span className="tg-dot">.</span></span>

        <nav className={`tg-views ${navOpen ? 'is-open' : ''}`} aria-label="Sections">
          <div className="tg-views-head">
            <span className="label">Sections</span>
            <button type="button" className="iconbtn" aria-label="Close" onClick={() => setNavOpen(false)}>
              <Icon name="close" size={14}/>
            </button>
          </div>
          {VIEWS.map(([id, label, icon]) => (
            <button key={id} type="button" className={`tg-tab ${view === id ? 'is-on' : ''}`}
                    aria-current={view === id ? 'page' : undefined} onClick={() => go(id)}>
              <Icon name={icon} size={14}/>
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="tg-head-right">
          {stats && (
            <span className="tg-hostline meta">
              {stats.hostname} · up {fmtUptime(stats.uptime)}
            </span>
          )}
          <button type="button" className="iconbtn" onClick={refresh} disabled={busy}
                  title="Refresh now" aria-label="Refresh now">
            <Icon name="refresh" size={16}/>
          </button>
          <button type="button" className="iconbtn" onClick={signOut} title="Sign out" aria-label="Sign out">
            <Icon name="logout" size={16}/>
          </button>
        </div>
      </header>

      {error && (
        <div className="alert alert--warn tg-banner">
          {error} <button type="button" className="btn btn--ghost btn--sm" onClick={refresh}>Try again</button>
        </div>
      )}

      <main className="tg-main">
        {!stats && !error && <div className="tg-loading"><span className="skeleton tg-skel"/></div>}
        {stats && view === 'overview' && <Overview {...shared} onOpen={go}/>}
        {stats && view === 'system' && <System {...shared}/>}
        {view === 'storage' && <Storage {...shared}/>}
        {stats && view === 'services' && <Services {...shared}/>}
        {view === 'games' && <Games {...shared}/>}
        {stats && view === 'processes' && <Processes {...shared}/>}
        {view === 'logs' && <Logs {...shared}/>}
      </main>

      {navOpen && <button type="button" className="tg-scrim" aria-label="Close sections" onClick={() => setNavOpen(false)}/>}

      <div className="toast-stack tg-toasts">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.kind}`}>
            <div className="toast-txt">{t.text}</div>
            <button type="button" className="toast-x" aria-label="Dismiss"
                    onClick={() => setToasts((l) => l.filter((x) => x.id !== t.id))}>
              <Icon name="close" size={12}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
